

































































































import {
  Component,
  Prop,
  PropSync,
  Ref,
  Vue,
  Watch
} from 'vue-property-decorator'
import { getModule } from 'vuex-module-decorators'
import userStore from '@/modules/auth/auth.store'
import matchStore from '@/modules/match/match.store'
import ServerError from '@/utils/server_error'
import { now } from '@/utils/time'
import {
  ResponseStatus,
  TournamentAPI,
  TournamentEntity
} from '@/api/tournament'
import {
  CheckInRoleType,
  MatchAPI,
  MatchGameEntity,
  MatchGameRosterEntity,
  MatchGameState,
  RelatedTournament
} from '@/api/match'
import ManageRosterDialog from '@/modules/match/components/ManageRosterDialog'

@Component({
  components: {
    ManageRosterDialog
  }
})
export default class JoinableMatchGameCard extends Vue {
  matchModule = getModule(matchStore, this.$store)
  userModule = getModule(userStore, this.$store)
  @PropSync('visible', { required: true }) isVisible!: boolean
  @Prop({ required: true }) matchGameId!: string
  @Prop({ required: true }) isCheckedIn!: boolean
  @Prop({ required: true }) isRosterConfirmed!: boolean
  @Prop({ required: true }) role!: CheckInRoleType
  @Prop({ type: String, default: '' }) captainName!: string
  @Prop({ default: null }) relatedTournament!: RelatedTournament | null

  matchGame: MatchGameEntity | null = null
  tournament: TournamentEntity | null = null
  checkInEndTimeLeft: string = ''
  now: number = 0
  tickTimer: NodeJS.Timeout | null = null
  numOfOpponent: number = 2 // FIXME: Should depend on tournament stage settings
  isImageError: boolean = false
  loading: boolean = false

  @Ref('card')
  readonly card!: HTMLDivElement

  @Watch('isVisible')
  async onVisibleChanged(isVisible: boolean) {
    if (isVisible) {
      await this.fetchMatchGame()

      if (this.relatedTournament) {
        await this.fetchTournament()
      }

      this.tickTimer = setInterval(this.onTick, 500)
      await this.onTick()

      window.addEventListener('click', this.onClickOutside)
    } else {
      if (this.tickTimer) clearInterval(this.tickTimer)
      window.removeEventListener('click', this.onClickOutside)
    }
  }

  @Watch('isLoading')
  onIsLoadingChanged(isLoading: boolean) {
    if (isLoading) {
      return
    }

    setTimeout(this.updateLayout)
  }

  /* Computed Properties */
  get checkInTooltipMessage(): string {
    return this.$t('JoinableMatchGameCard.OpponentNotReady')
  }

  get isLoading() {
    return !this.matchGame || (this.relatedTournament && !this.tournament)
  }

  get bannerSrc() {
    if (
      !this.tournament ||
      !this.tournament.detailBanner ||
      this.isImageError
    ) {
      return require('@/assets/default/tournament_background.png')
    }
    return this.tournament.detailBanner
  }

  get isDirectCheckInEnabled() {
    // for captain, can check in directly if checked-in before
    // for member, need to wait for captain check-in (isRosterConfirmed)
    if (this.role === CheckInRoleType.Member) return this.isRosterConfirmed
    else return this.isCheckedIn
  }

  get isShowWaitForCaptainMessage() {
    return this.role === CheckInRoleType.Member && !this.isRosterConfirmed
  }

  get isShowCaptainConfirmRoster() {
    return this.role === CheckInRoleType.Captain && !this.isRosterConfirmed
  }

  /* Methods */
  imageError() {
    this.isImageError = true
  }

  async fetchMatchGame() {
    try {
      const response = await MatchAPI.getMatchGame(this.matchGameId)
      let payload = response.data.payload
      if (response.data.status === ResponseStatus.Ok && payload) {
        this.matchGame = payload
      }
    } catch (err) {
      if (err instanceof ServerError) {
        this.$toast.error(err.getMessage())
      } else {
        this.$toast.error(this.$t('JoinableMatchGameCard.SystemErrorMessage'))
        console.error(err)
      }

      this.isVisible = false
    }
  }

  async fetchTournament() {
    try {
      const res = await TournamentAPI.getTournament(
        this.relatedTournament!.tournamentId
      )
      this.tournament = res.data.payload as TournamentEntity
    } catch (err) {
      if (err instanceof ServerError) {
        this.$toast.error(err.getMessage())
      } else {
        this.$toast.error(this.$t('JoinableMatchGameCard.SystemErrorMessage'))
        console.error(err)
      }

      this.isVisible = false
    }
  }

  async onTick() {
    this.now = now().getTime() / 1000

    if (!this.matchGame) {
      return
    }

    if (
      this.matchGame.checkInStartTime <= this.now &&
      this.matchGame.checkInEndTime >= this.now
    ) {
      this.updateCheckInEndTimeLeft()
    }
  }

  updateCheckInEndTimeLeft() {
    if (!this.matchGame) {
      return
    }

    const timeLeft = Math.round(this.matchGame.checkInEndTime - this.now)

    if (timeLeft < 0) {
      this.checkInEndTimeLeft = ''
      return
    }

    const hr = Math.floor(timeLeft / 60 / 60)
    const min = Math.floor(timeLeft / 60) - hr * 60
    const sec = timeLeft % 60

    this.checkInEndTimeLeft = `${hr
      .toString()
      .padStart(2, '0')}:${min
      .toString()
      .padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
  }

  participantTagAvatar(tag: string) {
    return tag.substring(0, 1)
  }

  onClickOutside(e: MouseEvent) {
    if (!this.card) return
    const children = this.card.getElementsByTagName('*')

    for (const child of children) {
      if (child === e.target) {
        return
      }
    }

    this.$emit('close')
  }

  get userId() {
    return this.userModule.userId
  }

  get isTeamCaptain() {
    if (!this.matchGame) {
      return false
    }

    return !!this.matchGame.checkIns.find(
      checkIn =>
        checkIn.member.memberId === this.userId &&
        checkIn.role === CheckInRoleType.Captain
    )
  }

  get isAllowedToManageRoster() {
    if (!this.isTeamCaptain) {
      return false
    }

    return this.matchGame!.state === MatchGameState.Waiting
  }

  updateLayout() {
    if (!this.card) {
      return
    }
    const bottomBarHeight = 60

    if (
      this.card.offsetTop + this.card.clientHeight >=
      window.innerHeight - bottomBarHeight
    ) {
      // use screen bottom as anchor
      this.card.style.top =
        'calc(-' + (window.innerHeight - bottomBarHeight) + 'px - 4rem)'
    }
  }

  async enterMatchRoom() {
    // skip if captain already checked in
    const me = this.matchGame!.checkIns.find(
      checkIn => checkIn.member.memberId === this.userModule.memberId
    )

    if (!me) {
      // fallback to confirm roster dialog if shit happens
      this.$emit('confirmRoster')
      return
    }

    if (me.isCheckedIn) {
      this.$emit('enterMatchRoom')
      return
    }

    // otherwise, check if roster length is equal to my team's check in length
    this.loading = true

    try {
      const res = await MatchAPI.getMatchGameRoster(this.matchGameId)
      const roster = res.data.payload as MatchGameRosterEntity

      const myTeamCheckIns = this.matchGame!.checkIns.filter(
        checkIn => checkIn.matchTeamId === me.matchTeamId
      )
      if (roster.players.length === myTeamCheckIns.length) {
        this.$emit('enterMatchRoom')
      } else {
        this.$emit('confirmRoster')
      }
    } catch (err) {
      if (err instanceof ServerError) {
        this.$toast.error(err.getMessage())
      } else {
        this.$toast.error('System error')
      }

      this.$emit('confirmRoster')
      console.error(err)
    } finally {
      this.loading = false
    }
  }
}
